import { create } from "zustand";
import apiClient, { setAuthToken } from "@/lib/api-client";
import { useNotificationStore } from "./notificationStore";
import { toast } from "sonner";

export type UserRole = "student" | "partner" | "admin" | "unassigned";

export interface User {
  id: string | number;
  name?: string;
  email: string;
  role: UserRole;
  avatar?: string;
  avatar_url?: string;
  university?: string;
  company?: string;
  status: "pending" | "active" | "rejected" | "completed";
  isDemo?: boolean;
  membershipTier?: "free" | "verified" | "premium";
  verificationStatus?: "unverified" | "pending_review" | "verified" | "rejected";
  two_factor_enabled?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  login: (email: string, password: string) => Promise<UserRole | "2fa_required">;
  socialLogin: (
    provider: "google" | "apple",
    idToken: string,
    name?: string,
  ) => Promise<UserRole | "2fa_required">;
  register: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  setUser: (user: User) => void;
  verify2FA: (code: string, trustDevice: boolean) => Promise<UserRole>;
}

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// /auth/login and /auth/me hand back the user row as-is, so the tier arrives as
// membership_tier and the verification as verification_status. The rest of the
// app reads both off the session user in camelCase. Normalising here is what
// makes a paid or admin-assigned tier visible in the student's own account:
// without it membershipTier stays undefined and every account renders as
// "Free Tier" with its gated features still locked.
export const normalizeAuthUser = (raw: Record<string, any>): User => {
  const merged: Record<string, any> = { ...raw };
  if (merged.membershipTier === undefined || merged.membershipTier === null) {
    merged.membershipTier = merged.membership_tier;
  }
  if (merged.verificationStatus === undefined || merged.verificationStatus === null) {
    merged.verificationStatus = merged.verification_status;
  }
  merged.avatar = merged.avatar || merged.avatar_url;
  return merged as User;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isHydrated: false,

  login: async (email, password) => {
    // Local Demo Mode Bypass
    if (email.startsWith("demo.") && email.endsWith("@taskpart.org")) {
      let role: UserRole = "student";
      if (email.toLowerCase().includes("partner")) role = "partner";
      if (email.toLowerCase().includes("admin")) role = "admin";

      const demoUser: User = {
        id: "demo-" + role,
        name: `Demo ${role.charAt(0).toUpperCase() + role.slice(1)}`,
        email: email,
        role: role,
        status: "active",
        university: role === "student" ? "UDSM" : undefined,
        company: role === "partner" ? "AzamPay" : undefined,
        isDemo: true,
      };

      set({ user: demoUser, isAuthenticated: true });
      localStorage.setItem("taskpart_demo_user", JSON.stringify(demoUser));
      useNotificationStore.getState().fetchNotifications();
      toast.success("Welcome to Demo Mode", {
        description: `Logged in as ${role}`,
      });
      return role;
    }

    const fingerprint = localStorage.getItem("taskpart_device_id") || crypto.randomUUID();
    localStorage.setItem("taskpart_device_id", fingerprint);

    const response = await apiClient.post("/auth/login", { 
      email, 
      password,
      device_fingerprint: fingerprint 
    });

    const { token, user } = response.data.data;

    if (token === "2fa_required") {
      set({ user: normalizeAuthUser({ ...user, isDemo: false }) });
      return "2fa_required" as any;
    }

    if (token) setAuthToken(token);
    localStorage.removeItem("taskpart_demo_user");

    set({
      user: normalizeAuthUser({ ...user, isDemo: false }),
      isAuthenticated: true
    });
    useNotificationStore.getState().fetchNotifications();
    toast.success("Login Successful", {
      description: `Welcome back, ${user.name || "User"}`,
    });
    return user.role;
  },

  socialLogin: async (provider, idToken, name) => {
    const fingerprint = localStorage.getItem("taskpart_device_id") || crypto.randomUUID();
    localStorage.setItem("taskpart_device_id", fingerprint);

    const response = await apiClient.post(`/auth/oauth/${provider}`, {
      id_token: idToken,
      device_fingerprint: fingerprint,
      ...(name ? { name } : {}),
    });

    const { token, user } = response.data.data;

    if (token === "2fa_required") {
      set({ user: normalizeAuthUser({ ...user, isDemo: false }) });
      return "2fa_required";
    }

    if (token) setAuthToken(token);
    localStorage.removeItem("taskpart_demo_user");

    set({
      user: normalizeAuthUser({ ...user, isDemo: false }),
      isAuthenticated: true,
    });
    useNotificationStore.getState().fetchNotifications();
    toast.success("Login Successful", {
      description: `Welcome, ${user.name || "User"}`,
    });
    return user.role;
  },

  register: async (data) => {
    const response = await apiClient.post("/auth/register", data);
    const { token, user } = response.data.data;
    if (token) setAuthToken(token);
    localStorage.removeItem("taskpart_demo_user");
    set({
      user: normalizeAuthUser({ ...user, isDemo: false }),
      isAuthenticated: true
    });
    useNotificationStore.getState().fetchNotifications();
    toast.success("Account Created", {
      description: "Welcome to the Taskpart Network!",
    });
  },

  logout: async () => {
    const isDemo = !!localStorage.getItem("taskpart_demo_user");
    localStorage.removeItem("taskpart_demo_user");

    if (isDemo) {
      setAuthToken(null);
      set({ user: null, isAuthenticated: false });
      return;
    }

    // Revoke server-side first, while the token is still attached. The endpoint
    // now sits behind auth and denylists the presented token's jti, so calling it
    // after the token has been cleared would send no credentials and leave the
    // session alive on the server until it expired.
    try {
      await apiClient.post("/auth/logout", undefined, {
        // A rejected revocation is expected when the token has already expired
        // or been revoked elsewhere. Letting it reach the global handler would
        // fire an auth:unauthorized redirect that races with the local sign-out
        // below and can strand the user on a half-cleared session.
        skipGlobalErrorHandler: true,
      } as any);
    } catch {
      // A failed revocation must not trap the user in a half-signed-out state:
      // the local session is still cleared below, and the token expires on its
      // own regardless.
    } finally {
      setAuthToken(null);
      set({ user: null, isAuthenticated: false });
      toast.info("Logged out", {
        description: "You have been securely signed out.",
      });
    }
  },

  checkSession: async () => {
    // Check for demo user first
    const savedDemoUser = localStorage.getItem("taskpart_demo_user");
    if (savedDemoUser) {
      try {
        const user = JSON.parse(savedDemoUser);
        set({ user: { ...user, isDemo: true }, isAuthenticated: true, isHydrated: true });
        return;
      } catch (e) {
        localStorage.removeItem("taskpart_demo_user");
      }
    }

    try {
      const response = await apiClient.get("/auth/me");
      const userData = response.data.data;
      set({
        user: normalizeAuthUser({ ...userData, isDemo: false }),
        isAuthenticated: true,
        isHydrated: true,
      });
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        setAuthToken(null);
      }
      set({ user: null, isAuthenticated: false, isHydrated: true });
    }
  },

  setUser: (user) => set({ user: normalizeAuthUser(user as Record<string, any>), isAuthenticated: true }),
  verify2FA: async (code: string, trustDevice: boolean): Promise<UserRole> => {
    const fingerprint = localStorage.getItem("taskpart_device_id");
    const user = get().user;
    
    const response = await apiClient.post("/auth/login/verify-2fa", {
      code,
      trust_device: trustDevice,
      user_id: user?.id,
    }, {
      headers: { "X-Device-Fingerprint": fingerprint || "" }
    });

    const { user: verifiedUser, token } = response.data.data;
    if (token) setAuthToken(token);
    set({
      user: normalizeAuthUser({ ...verifiedUser, isDemo: false }),
      isAuthenticated: true
    });
    useNotificationStore.getState().fetchNotifications();
    toast.success("Login Successful");
    return verifiedUser.role;
  },
}));
